/**
 * El vencimiento que se PROPONE al hacer una factura — `OD-0051 D-07` (ACCEPTED).
 *
 * Sólo se propone; nunca se impone. Va en el campo, a la vista y editable, y
 * la API lo valida igual que si lo hubiera escrito el usuario. Una factura que
 * se guarda sin vencimiento sigue sin vencimiento: el servidor no inventa
 * ninguno (`OD-0051 D-03`).
 *
 * ```text
 * a quién   a un cliente que CONSTA como empresa o profesional:
 *             NIF de entidad, NIF-IVA de la UE, o pagador obligado a
 *             retener (art. 76 RIRPF, OD-0029 D-03)
 *           NO a un NIF de persona sin más: puede ser un consumidor, y a
 *             un consumidor la Ley 3/2004 no le pone plazo
 * cuánto    30 días, el plazo supletorio de la Ley 3/2004, art. 4.1
 * desde     hoy, en Madrid: el día en que se hace la factura
 * ```
 *
 * Y si el cliente tiene su PLAZO DE PAGO puesto en la ficha (`OD-0062` E-03),
 * manda ese, sea empresa o particular: hoy + su plazo, y 0 es al contado. Sin
 * plazo, la regla de arriba.
 *
 * Aquí no hay dinero: sólo días de calendario. Y ninguna conversión a número, que la web
 * tiene prohibido (`tests/adversarial/web-shell.mjs`).
 */

/** Ley 3/2004, art. 4.1: sin pacto, 30 días naturales. */
export const PLAZO_PROPUESTO_DIAS = 30;

type ClienteConTipo = {
  readonly taxIdType: string | null;
  readonly withholdingObligation: string;
  /** `OD-0062` E-03: el plazo de su ficha, en días. Ausente o `null`, sin plazo. */
  readonly paymentTermsDays?: number | null;
};

/** Si el cliente CONSTA como empresa o profesional. Ante la duda, no. */
export function constaComoEmpresa(cliente: ClienteConTipo): boolean {
  return cliente.taxIdType === "NIF_ES_ENTITY" || cliente.taxIdType === "VAT_EU" || cliente.withholdingObligation === "SI";
}

/** `fecha` (AAAA-MM-DD) más `dias` naturales. En UTC no hay cambio de hora. */
export function sumarDias(fecha: string, dias: number): string {
  const d = new Date(`${fecha}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

/** El vencimiento que se propone para `cliente` a fecha `hoy`, o `null` si ninguno. */
export function vencimientoPropuesto(cliente: ClienteConTipo | null, hoy: string): string | null {
  if (cliente === null) return null;
  if (typeof cliente.paymentTermsDays === "number") return sumarDias(hoy, cliente.paymentTermsDays);
  if (!constaComoEmpresa(cliente)) return null;
  return sumarDias(hoy, PLAZO_PROPUESTO_DIAS);
}

/**
 * Qué vencimiento queda al ELEGIR cliente.
 *
 * Lo que escribió el usuario no se toca nunca. Sólo se cambia un campo vacío o
 * uno que todavía tiene la propuesta anterior sin tocar: si pasa de una empresa
 * a un particular, la propuesta se retira en vez de quedarse puesta.
 */
export function vencimientoAlElegirCliente(p: {
  readonly actual: string | null;
  readonly propuestoAntes: string | null;
  readonly cliente: ClienteConTipo | null;
  readonly hoy: string;
}): { readonly dueDate: string | null; readonly propuesto: string | null } {
  const libre = p.actual === null || p.actual === "" || p.actual === p.propuestoAntes;
  if (!libre) return { dueDate: p.actual, propuesto: null };
  const nuevo = vencimientoPropuesto(p.cliente, p.hoy);
  return { dueDate: nuevo, propuesto: nuevo };
}
